import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Chip, Grid, Card, CardContent, Button, AppBar, Toolbar,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Select, MenuItem, FormControl, InputLabel, FormControlLabel,
    RadioGroup, Radio, FormLabel, Alert, CircularProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsIcon from '@mui/icons-material/Settings';
import TenantManagementDrawer from './TenantManagementDrawer';
import { apiFetch, getBaseDomain } from '../api';


const SuperAdminDashboard = ({ onLogout }) => {
    const [data, setData] = useState({ stats: {}, tenants: [] });
    const [loading, setLoading] = useState(true);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [formData, setFormData] = useState({
        environment: 'localhost',
        id: '',
        company_name: '',
        owner_name: '',
        email: '',
        password: '',
        plan_name: 'Standard',
        tenancy_db_name: '',
        db_username: '',
        db_password: '',
    });

    const handleSubdomainChange = (val) => {
        const cleanSubdomain = val.toLowerCase().replace(/[^a-z0-9_-]/g, '');
        setFormData(prev => {
            const updates = { ...prev, id: cleanSubdomain };
            if (prev.environment === 'localhost') {
                updates.tenancy_db_name = `rental_${cleanSubdomain}`;
            } else {
                updates.tenancy_db_name = `u905726705_${cleanSubdomain}`;
                updates.db_username = `u905726705_${cleanSubdomain}`;
            }
            return updates;
        });
    };

    const handleEnvChange = (env) => {
        setFormData(prev => {
            const updates = { ...prev, environment: env };
            if (env === 'localhost') {
                updates.tenancy_db_name = prev.id ? `rental_${prev.id}` : '';
                updates.db_username = '';
                updates.db_password = '';
            } else {
                updates.tenancy_db_name = prev.id ? `u905726705_${prev.id}` : '';
                updates.db_username = prev.id ? `u905726705_${prev.id}` : '';
            }
            return updates;
        });
    };

    const handleCreateTenant = async (e) => {
        e.preventDefault();
        setErrorMsg('');
        setSuccessMsg('');
        setCreateLoading(true);

        try {
            const response = await apiFetch('/api/admin/tenants', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
                credentials: 'include'
            });

            const result = await response.json();
            if (response.ok) {
                setSuccessMsg(`Tenant created successfully! Subdomain: ${result.domain}`);
                fetchData();
                setTimeout(() => {
                    setCreateModalOpen(false);
                    // Reset form
                    setFormData({
                        environment: 'localhost',
                        id: '',
                        company_name: '',
                        owner_name: '',
                        email: '',
                        password: '',
                        plan_name: 'Standard',
                        tenancy_db_name: '',
                        db_username: '',
                        db_password: '',
                    });
                    setSuccessMsg('');
                }, 3000);
            } else {
                setErrorMsg(result.message || 'Tenant creation failed.');
            }
        } catch (error) {
            console.error('Error creating tenant:', error);
            setErrorMsg('Something went wrong. Please check console.');
        } finally {
            setCreateLoading(false);
        }
    };

    const openManage = (tenant) => {
        setSelectedTenant(tenant);
        setDrawerOpen(true);
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await apiFetch('/api/admin/dashboard', {
                credentials: 'include'
            });

            const result = await response.json();
            setData(result);
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
        setLoading(false);
    };

    const toggleStatus = async (id) => {
        try {
            await apiFetch(`/api/admin/tenants/${id}/toggle-status`, {
                method: 'POST',
                credentials: 'include'
            });

            fetchData();
        } catch (error) {
            console.error('Error toggling status:', error);
        }
    };

    const updateLimits = async (tenant) => {
        const newUsers = prompt(`Enter Max Users for ${tenant.company_name}:`, tenant.max_users);
        const newBranches = prompt(`Enter Max Branches for ${tenant.company_name}:`, tenant.max_branches);
        const newPlan = prompt(`Enter Plan Name (Standard/Pro) for ${tenant.company_name}:`, tenant.plan_name);

        if (newUsers !== null && newBranches !== null && newPlan !== null) {
            try {
                await apiFetch(`/api/admin/tenants/${tenant.id}/update-limits`, {
                    method: 'POST',
                    credentials: 'include',

                    body: JSON.stringify({
                        max_users: parseInt(newUsers),
                        max_branches: parseInt(newBranches),
                        plan_name: newPlan
                    })
                });
                fetchData();
            } catch (error) {
                console.error('Error updating limits:', error);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <RefreshIcon sx={{ fontSize: 60, color: '#102A43', animation: 'spin 2s linear infinite' }} />
                <Typography variant="h6" color="textSecondary">डेटा लोड होत आहे, कृपया थांबा...</Typography>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </Box>
        );
    }

    return (
        <>
            <Box sx={{ minHeight: '100vh', backgroundColor: '#F0F4F8' }}>
                <AppBar position="static" sx={{ backgroundColor: '#102A43', elevation: 0 }}>
                    <Toolbar>
                        <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                            CSM PRO | CENTRAL CONTROL
                        </Typography>
                        <IconButton color="inherit" onClick={fetchData}>
                            <RefreshIcon />
                        </IconButton>
                        <Button color="inherit" onClick={onLogout} startIcon={<PowerSettingsNewIcon />}>
                            Logout
                        </Button>
                    </Toolbar>
                </AppBar>

                <Box sx={{ p: 4 }}>
                    <Typography variant="h4" sx={{ color: '#102A43', fontWeight: 'bold', mb: 4 }}>
                        Super Admin Dashboard
                    </Typography>

                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderLeft: '5px solid #102A43' }}>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>Total Tenants</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                        <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        {data?.stats?.total_tenants || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderLeft: '5px solid #0FA958' }}>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>Active Clients</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1B5E20' }}>
                                        <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        {data?.stats?.active_tenants || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderLeft: '5px solid #FF9800' }}>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>Standard Plans</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                        {data?.stats?.standard_plans || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderLeft: '5px solid #2196F3' }}>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>Pro Plans</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                        {data?.stats?.pro_plans || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Paper sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, backgroundColor: '#243B53', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">Manage Clients</Typography>
                            <Button 
                                variant="contained" 
                                color="success" 
                                onClick={() => setCreateModalOpen(true)}
                                sx={{ fontWeight: 'bold' }}
                            >
                                + Create New Tenant
                            </Button>
                        </Box>
                        <TableContainer>
                            <Table>
                                <TableHead sx={{ backgroundColor: '#F0F4F8' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Company / ID</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Users</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Branches</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Subscription</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data?.tenants?.length > 0 ? data.tenants.map((tenant) => {
                                        // Subscription expiry logic
                                        let subLabel = 'Lifetime';
                                        let subColor = 'default';
                                        let subBg = '#E8F5E9';
                                        let subText = '#2E7D32';
                                        if (tenant.subscription_expires_at) {
                                            const expiry = new Date(tenant.subscription_expires_at);
                                            const today = new Date();
                                            const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
                                            const dd = String(expiry.getDate()).padStart(2, '0');
                                            const mm = String(expiry.getMonth() + 1).padStart(2, '0');
                                            const yyyy = expiry.getFullYear();
                                            subLabel = `${dd}/${mm}/${yyyy}`;
                                            if (daysLeft < 0) { subBg = '#FFEBEE'; subText = '#C62828'; subLabel += ' (Expired)'; }
                                            else if (daysLeft <= 30) { subBg = '#FFF8E1'; subText = '#E65100'; subLabel += ` (${daysLeft}d)`; }
                                            else { subBg = '#E8F5E9'; subText = '#2E7D32'; subLabel += ` (${daysLeft}d)`; }
                                        }
                                        return (
                                            <TableRow key={tenant.id} hover>
                                                <TableCell>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{tenant.company_name}</Typography>
                                                    <Typography variant="caption" color="textSecondary">{tenant.id}.{getBaseDomain()}</Typography>
                                                </TableCell>

                                                <TableCell>{tenant.email}</TableCell>
                                                <TableCell>
                                                    <Chip label={tenant.plan_name} color={tenant.plan_name === 'Pro' ? 'primary' : 'default'} variant="outlined" size="small" />
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>
                                                    <Chip label={tenant.max_users === -1 ? '∞' : tenant.max_users} size="small" />
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center' }}>
                                                    <Chip label={tenant.max_branches === -1 ? '∞' : tenant.max_branches} size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={subLabel}
                                                        size="small"
                                                        sx={{ bgcolor: subBg, color: subText, fontWeight: 'bold', fontSize: '0.75rem' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip label={tenant.is_active ? 'Active' : 'Inactive'} color={tenant.is_active ? 'success' : 'error'} size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button size="small" variant="contained" color={tenant.is_active ? 'error' : 'success'} onClick={() => toggleStatus(tenant.id)} sx={{ minWidth: 90 }}>
                                                            {tenant.is_active ? 'Disable' : 'Enable'}
                                                        </Button>
                                                        <Button
                                                            size="small" variant="contained"
                                                            startIcon={<SettingsIcon />}
                                                            onClick={() => openManage(tenant)}
                                                            sx={{ bgcolor: '#102A43', '&:hover': { bgcolor: '#0a1e30' } }}
                                                        >
                                                            Manage
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }) : (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                                No tenants found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </Box>

            {/* Tenant Management Drawer */}
            <TenantManagementDrawer
                tenant={selectedTenant}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            />

            {/* Create Tenant Modal */}
            <Dialog 
                open={createModalOpen} 
                onClose={() => !createLoading && setCreateModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <form onSubmit={handleCreateTenant}>
                    <DialogTitle sx={{ backgroundColor: '#102A43', color: 'white', fontWeight: 'bold' }}>
                        Create New Tenant / Client Instance
                    </DialogTitle>
                    <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        
                        {errorMsg && <Alert severity="error" sx={{ my: 1 }}>{errorMsg}</Alert>}
                        {successMsg && <Alert severity="success" sx={{ my: 1 }}>{successMsg}</Alert>}

                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontWeight: 'bold', color: '#102A43', mb: 1 }}>Target Environment</FormLabel>
                            <RadioGroup
                                row
                                name="environment"
                                value={formData.environment}
                                onChange={(e) => handleEnvChange(e.target.value)}
                            >
                                <FormControlLabel value="localhost" control={<Radio color="primary" />} label="Localhost (Development)" />
                                <FormControlLabel value="online" control={<Radio color="success" />} label="Online (Production / Hostinger)" />
                            </RadioGroup>
                        </FormControl>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Subdomain / ID"
                                    placeholder="e.g. shreehirasuppliers"
                                    value={formData.id}
                                    onChange={(e) => handleSubdomainChange(e.target.value)}
                                    fullWidth
                                    required
                                    helperText={`URL: ${formData.id ? formData.id : 'subdomain'}.${formData.environment === 'localhost' ? 'localhost' : 'esensevvg.online'}`}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Company Name"
                                    placeholder="e.g. Shree Hira Suppliers"
                                    value={formData.company_name}
                                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                    fullWidth
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Owner / Admin Name"
                                    placeholder="e.g. Suhas Gaikwad"
                                    value={formData.owner_name}
                                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Owner / Admin Email"
                                    type="email"
                                    placeholder="e.g. admin@shreehira.com"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    fullWidth
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Login Password"
                                    type="password"
                                    placeholder="Minimum 6 characters"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="plan-select-label">Subscription Plan</InputLabel>
                                    <Select
                                        labelId="plan-select-label"
                                        value={formData.plan_name}
                                        label="Subscription Plan"
                                        onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                                    >
                                        <MenuItem value="Standard">Standard (1 User, 1 Branch)</MenuItem>
                                        <MenuItem value="Pro">Pro (10 Users, 5 Branches)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#243B53', mb: 1, borderBottom: '1px solid #ddd', pb: 0.5 }}>
                                    Database Configuration
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={formData.environment === 'localhost' ? 12 : 4}>
                                <TextField
                                    label="Database Name"
                                    value={formData.tenancy_db_name}
                                    onChange={(e) => setFormData({ ...formData, tenancy_db_name: e.target.value })}
                                    fullWidth
                                    required
                                    helperText={formData.environment === 'localhost' ? "लोकलहोस्टवर हा डेटाबेस सिस्टीम आपोआप स्वतः तयार करेल." : "हा डेटाबेस ऑनलाईन होस्टवर आधीच मॅन्युअली बनवलेला असावा."}
                                />
                            </Grid>

                            {formData.environment === 'online' && (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Database Username"
                                            value={formData.db_username}
                                            onChange={(e) => setFormData({ ...formData, db_username: e.target.value })}
                                            fullWidth
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label="Database Password"
                                            type="password"
                                            value={formData.db_password}
                                            onChange={(e) => setFormData({ ...formData, db_password: e.target.value })}
                                            fullWidth
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, backgroundColor: '#F0F4F8' }}>
                        <Button 
                            onClick={() => setCreateModalOpen(false)} 
                            disabled={createLoading}
                            variant="outlined"
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            disabled={createLoading}
                            variant="contained"
                            color="success"
                            startIcon={createLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {createLoading ? 'Creating & Seeding...' : 'Create & Initialize'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
};

export default SuperAdminDashboard;
